<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Loading and Error States -->
    <div v-if="loading" class="flex items-center justify-center h-screen">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
    </div>
    <div v-else-if="error" class="text-center text-red-300">
      <p>{{ error }}</p>
    </div>
    <div v-else class="space-y-6">
      <!-- Header -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-4 rounded-lg shadow-xl">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 class="text-xl font-semibold text-indigo-300">ระบบ Migrate ผู้เล่น</h2>
            <p class="text-sm text-gray-400 mt-1">UID: <span class="font-mono">{{ userId }}</span></p>
          </div>
          <div class="flex gap-2">
            <router-link
              to="/admin/migrate-players"
              class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              <i class="fas fa-arrow-left mr-1"></i> กลับไปรายการ
            </router-link>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 rounded-lg shadow-xl overflow-hidden">
        <div class="flex border-b border-gray-700 overflow-x-auto">
          <button
            v-for="t in tabs"
            :key="t.id"
            @click="activeTab = t.id"
            :class="[
              'px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors',
              activeTab === t.id
                ? 'bg-indigo-600 text-white border-b-2 border-indigo-400'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            ]"
          >
            <i :class="t.icon + ' mr-1'"></i> {{ t.name }}
          </button>
        </div>

        <div class="p-4">
          <!-- TAB 1: Item Migrate -->
          <div v-if="activeTab === 'item-migrate'" class="space-y-6">
            <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <h3 class="text-lg font-semibold text-indigo-300 mb-4">
                <i class="fas fa-box-open mr-2"></i>สถานะ Migrate ไอเทม
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="bg-gray-700 p-3 rounded-md">
                  <p class="text-sm text-gray-400">migratedToItemV2</p>
                  <p :class="userData?.migratedToItemV2 ? 'text-green-400' : 'text-red-400'" class="font-semibold">
                    {{ userData?.migratedToItemV2 ? '✅ ย้ายแล้ว' : '❌ ยังไม่ย้าย' }}
                  </p>
                </div>
                <div class="bg-gray-700 p-3 rounded-md">
                  <p class="text-sm text-gray-400">สถานะ Schema เก่า</p>
                  <p :class="!hasOldItemSchema ? 'text-green-400' : 'text-red-400'" class="font-semibold">
                    {{ !hasOldItemSchema ? '✅ ไม่พบข้อมูลเก่า' : '❌ พบข้อมูลเก่า' }}
                  </p>
                </div>
              </div>

              <div class="mt-4 flex flex-wrap gap-3">
                <button
                  v-if="!userData?.migratedToItemV2 || hasOldItemSchema"
                  @click="runItemMigrate"
                  :disabled="isUpdating"
                  class="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  <i class="fas fa-magic mr-1"></i>
                  {{ isUpdating ? 'กำลังย้าย...' : 'ย้ายข้อมูลไอเทมเป็น V2' }}
                </button>
                <button
                  @click="resetItemMigrate"
                  :disabled="isUpdating"
                  class="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  <i class="fas fa-undo mr-1"></i> รีเซ็ตให้ผู้เล่นย้ายเอง
                </button>
              </div>
              <p v-if="migrateMessage" :class="migrateError ? 'text-red-400' : 'text-green-400'" class="mt-3 text-sm">
                {{ migrateMessage }}
              </p>
            </div>
          </div>

          <!-- TAB 2: Item Verify -->
          <div v-if="activeTab === 'item-verify'" class="space-y-6">
            <!-- Character Summary -->
            <div v-if="characterData && characterPrivateData" class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
              <h3 class="text-xl font-semibold text-indigo-300 mb-3">สถานะตัวละคร</h3>
              <div class="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div class="flex flex-col p-2 rounded-lg shadow-md bg-gray-800">
                  <div class="flex justify-between items-center space-x-3">
                    <div class="flex items-center space-x-2">
                      <i class="fa-solid fa-heart text-red-500 text-sm"></i>
                      <span class="font-bold text-sm text-white">{{ playerHp }}/{{ playerMaxHp }}</span>
                    </div>
                    <div class="text-xs text-gray-400">พลังชีวิต</div>
                  </div>
                  <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
                    <div class="bg-red-500 h-2 rounded-full" :style="{ width: `${hpPercentage}%` }"></div>
                  </div>
                </div>
                <div class="flex flex-col p-2 rounded-lg shadow-md bg-gray-800">
                  <div class="flex justify-between items-center space-x-3">
                    <div class="flex items-center space-x-2">
                      <i class="fa-solid fa-bolt text-yellow-400 text-sm"></i>
                      <span class="font-bold text-sm text-white">{{ characterPrivateData.energyLevel || 0 }}/4800</span>
                    </div>
                    <div class="text-xs text-gray-400">พลังงาน</div>
                  </div>
                  <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
                    <div class="bg-yellow-500 h-2 rounded-full" :style="{ width: `${energyPercentage}%` }"></div>
                  </div>
                </div>
                <div class="flex flex-col p-2 rounded-lg shadow-md bg-gray-800">
                  <div class="flex justify-between items-center space-x-3">
                    <div class="flex items-center space-x-2">
                      <i class="fa-solid fa-drumstick-bite text-green-400 text-sm"></i>
                      <span class="font-bold text-sm text-white">{{ characterPrivateData.foodLevel || 0 }}/100</span>
                    </div>
                    <div class="text-xs text-gray-400">อาหาร</div>
                  </div>
                  <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
                    <div class="bg-green-500 h-2 rounded-full" :style="{ width: `${foodPercentage}%` }"></div>
                  </div>
                </div>
                <div class="flex flex-col p-2 rounded-lg shadow-md bg-gray-800">
                  <div class="flex justify-between items-center space-x-3">
                    <div class="flex items-center space-x-2">
                      <i class="fa-solid fa-smile text-blue-400 text-sm"></i>
                      <span class="font-bold text-sm text-white">{{ characterPrivateData.happyLevel || 0 }}/100</span>
                    </div>
                    <div class="text-xs text-gray-400">ความสุข</div>
                  </div>
                  <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
                    <div class="bg-blue-500 h-2 rounded-full" :style="{ width: `${happinessPercentage}%` }"></div>
                  </div>
                </div>
                <div class="flex flex-col p-2 rounded-lg shadow-md bg-gray-800">
                  <div class="flex justify-between items-center space-x-3">
                    <div class="flex items-center space-x-2">
                      <i class="fa-solid fa-biohazard text-purple-400 text-sm"></i>
                      <span class="font-bold text-sm text-white">{{ characterPrivateData.infectionLevel || 0 }}/100</span>
                    </div>
                    <div class="text-xs text-gray-400">เชื้อโรค</div>
                  </div>
                  <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
                    <div class="bg-purple-500 h-2 rounded-full" :style="{ width: `${infectionPercentage}%` }"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stats Comparisons -->
            <div v-if="characterPrivateData" class="space-y-4">
              <h3 class="text-lg font-semibold text-indigo-300">ตรวจสอบความถูกต้องของ Stats</h3>

              <!-- Item Stats -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Item Stats</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div class="bg-gray-700 p-3 rounded-md">
                    <h5 class="font-semibold text-indigo-300 text-sm">Item Stats (จาก characterPrivate)</h5>
                    <div class="text-indigo-200 space-y-1 mt-1">
                      <p v-for="stat in statKeys" :key="stat">
                        {{ statLabels[stat] }}: {{ characterPrivateData.itemStats?.[stat] || 0 }}
                      </p>
                    </div>
                  </div>
                  <div class="bg-gray-700 p-3 rounded-md">
                    <h5 class="font-semibold text-indigo-300 text-sm">Calculated Item Stats</h5>
                    <div class="text-indigo-200 space-y-1 mt-1">
                      <p v-for="stat in statKeys" :key="stat">
                        {{ statLabels[stat] }}: {{ calculatedItemStats[stat] || 0 }}
                      </p>
                    </div>
                  </div>
                </div>
                <p :class="isItemStatsMatching ? 'text-green-400' : 'text-red-400'" class="mt-3 text-sm">
                  {{ isItemStatsMatching ? '✅ Item Stats ตรงกัน' : '❌ Item Stats ไม่ตรงกัน' }}
                </p>
                <button v-if="!isItemStatsMatching" @click="updateItemStats" :disabled="isUpdating" class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg mt-2 transition-colors disabled:opacity-50">
                  อัปเดต Item Stats
                </button>
              </div>

              <!-- Ghost Stats -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Ghost Stats</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div class="bg-gray-700 p-3 rounded-md">
                    <h5 class="font-semibold text-indigo-300 text-sm">Ghost Stats (จาก characterPrivate)</h5>
                    <div class="text-indigo-200 space-y-1 mt-1">
                      <p v-for="stat in statKeys" :key="stat">
                        {{ statLabels[stat] }}: {{ characterPrivateData.ghostStats?.[stat] || 0 }}
                      </p>
                    </div>
                  </div>
                  <div class="bg-gray-700 p-3 rounded-md">
                    <h5 class="font-semibold text-indigo-300 text-sm">Calculated Ghost Stats</h5>
                    <div class="text-indigo-200 space-y-1 mt-1">
                      <p v-for="stat in statKeys" :key="stat">
                        {{ statLabels[stat] }}: {{ calculatedGhostStats[stat] || 0 }}
                      </p>
                    </div>
                  </div>
                </div>
                <p :class="isGhostStatsMatching ? 'text-green-400' : 'text-red-400'" class="mt-3 text-sm">
                  {{ isGhostStatsMatching ? '✅ Ghost Stats ตรงกัน' : '❌ Ghost Stats ไม่ตรงกัน' }}
                </p>
                <button v-if="!isGhostStatsMatching" @click="updateGhostStats" :disabled="isUpdating" class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg mt-2 transition-colors disabled:opacity-50">
                  อัปเดต Ghost Stats
                </button>
              </div>

              <!-- Bonus Stats -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Bonus Stats (Set Items)</h4>
                <div class="text-xs mb-2">
                  <p class="text-indigo-200">จำนวนไอเทมที่สวมใส่: {{ equippedItems.length }}</p>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div class="bg-gray-700 p-3 rounded-md">
                    <h5 class="font-semibold text-indigo-300 text-sm">Bonus Stats (จาก characterPrivate)</h5>
                    <div class="text-indigo-200 space-y-1 mt-1">
                      <p v-for="stat in statKeys" :key="stat">
                        {{ statLabels[stat] }}: {{ characterPrivateData.bonusStats?.[stat] || 0 }}
                      </p>
                    </div>
                  </div>
                  <div class="bg-gray-700 p-3 rounded-md">
                    <h5 class="font-semibold text-indigo-300 text-sm">Calculated Bonus Stats</h5>
                    <div class="text-indigo-200 space-y-1 mt-1">
                      <p v-for="stat in statKeys" :key="stat">
                        {{ statLabels[stat] }}: {{ calculatedBonusStats[stat] || 0 }}
                      </p>
                    </div>
                  </div>
                </div>
                <p :class="isBonusStatsMatching ? 'text-green-400' : 'text-red-400'" class="mt-3 text-sm">
                  {{ isBonusStatsMatching ? '✅ Bonus Stats ตรงกัน' : '❌ Bonus Stats ไม่ตรงกัน' }}
                </p>
                <button v-if="!isBonusStatsMatching" @click="updateBonusStats" :disabled="isUpdating" class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg mt-2 transition-colors disabled:opacity-50">
                  อัปเดต Bonus Stats
                </button>
              </div>

              <!-- Bag Weight -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบน้ำหนักกระเป๋า</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div class="bg-gray-700 p-3 rounded-md">
                    <p>น้ำหนักปัจจุบัน: {{ currentWeight }} / {{ maxLoad }}</p>
                  </div>
                  <div class="bg-gray-700 p-3 rounded-md">
                    <p>น้ำหนักที่คำนวณได้: {{ calculatedBagWeight }}</p>
                  </div>
                </div>
                <p :class="isBagWeightMatching ? 'text-green-400' : 'text-red-400'" class="mt-3 text-sm">
                  {{ isBagWeightMatching ? '✅ น้ำหนักกระเป๋าตรงกัน' : '❌ น้ำหนักกระเป๋าไม่ตรงกัน' }}
                </p>
              </div>

              <!-- Storage Weight -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบน้ำหนักคลัง</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div class="bg-gray-700 p-3 rounded-md">
                    <p>น้ำหนักปัจจุบัน: {{ storageWeight }} / {{ storageMaxLoad }}</p>
                  </div>
                  <div class="bg-gray-700 p-3 rounded-md">
                    <p>น้ำหนักที่คำนวณได้: {{ calculatedStorageWeight }}</p>
                  </div>
                </div>
                <p :class="isStorageWeightMatching ? 'text-green-400' : 'text-red-400'" class="mt-3 text-sm">
                  {{ isStorageWeightMatching ? '✅ น้ำหนักคลังตรงกัน' : '❌ น้ำหนักคลังไม่ตรงกัน' }}
                </p>
              </div>

              <!-- Inventory -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">
                  กระเป๋า ({{ inventoryItems.length }} ชิ้น) — น้ำหนัก {{ currentWeight }}/{{ maxLoad }}
                </h4>
                <div v-if="inventoryItems.length" class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                  <div
                    v-for="item in inventoryItems"
                    :key="item.id"
                    class="bg-gray-700 p-2 rounded-md border border-gray-600 text-center"
                  >
                    <img :src="item.img" class="w-10 h-10 mx-auto object-cover rounded" @error="$event.target.style.display='none'" />
                    <p class="text-xs mt-1 truncate">{{ item.name }}</p>
                    <p class="text-xxs text-gray-400">x{{ item.quantity }}</p>
                  </div>
                </div>
                <p v-else class="text-gray-400 text-sm">กระเป๋าว่างเปล่า</p>
              </div>

              <!-- Storage -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">
                  คลังเก็บของ ({{ storageItems.length }} ชิ้น) — น้ำหนัก {{ storageWeight }}/{{ storageMaxLoad }}
                </h4>
                <div v-if="storageItems.length" class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                  <div
                    v-for="item in storageItems"
                    :key="item.id"
                    class="bg-gray-700 p-2 rounded-md border border-gray-600 text-center"
                  >
                    <img :src="item.img" class="w-10 h-10 mx-auto object-cover rounded" @error="$event.target.style.display='none'" />
                    <p class="text-xs mt-1 truncate">{{ item.name }}</p>
                    <p class="text-xxs text-gray-400">x{{ item.quantity }}</p>
                  </div>
                </div>
                <p v-else class="text-gray-400 text-sm">คลังว่างเปล่า</p>
              </div>

              <!-- Equipped Items -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">ไอเทมที่สวมใส่ ({{ equippedItems.length }} ชิ้น)</h4>
                <div v-if="equippedItems.length" class="flex flex-wrap gap-2">
                  <div
                    v-for="item in equippedItems"
                    :key="item.id"
                    class="bg-gray-700 px-3 py-1 rounded-full text-xs border border-gray-600"
                  >
                    {{ item.name }}
                  </div>
                </div>
                <p v-else class="text-gray-400 text-sm">ไม่มีไอเทมที่สวมใส่</p>
              </div>

              <!-- Set Bonuses Progress -->
              <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 class="text-sm font-semibold text-indigo-300 mb-3">ความคืบหน้า Set Items</h4>
                <div v-if="setProgress.length" class="space-y-2">
                  <div v-for="set in setProgress" :key="set.name" class="bg-gray-700 p-3 rounded-md">
                    <div class="flex justify-between items-center">
                      <span class="text-sm font-semibold">{{ set.name }}</span>
                      <span class="text-xs">{{ set.equippedCount }}/{{ set.totalItems }} ชิ้น</span>
                    </div>
                    <div class="w-full bg-gray-600 rounded-full h-2 mt-1">
                      <div class="bg-indigo-500 h-2 rounded-full" :style="{ width: `${(set.equippedCount / set.totalItems) * 100}%` }"></div>
                    </div>
                    <p class="text-xs text-gray-300 mt-1">โบนัสปัจจุบัน: {{ formatBonusObject(set.currentBonus) }}</p>
                  </div>
                </div>
                <p v-else class="text-gray-400 text-sm">ไม่พบ Set Items ที่สวมใส่</p>
              </div>
            </div>
          </div>

          <!-- TAB 3: Camp & Villagers -->
          <div v-if="activeTab === 'camp-villagers'" class="space-y-6">
            <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <h3 class="text-lg font-semibold text-indigo-300 mb-4">
                <i class="fas fa-campground mr-2"></i>ข้อมูลแคมป์
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div class="bg-gray-700 p-3 rounded-md">
                  <p class="text-sm text-gray-400">Schema Version</p>
                  <p :class="campData?.villagerSchemaVersion === 2 ? 'text-green-400' : 'text-red-400'" class="font-semibold">
                    {{ campData?.villagerSchemaVersion === 2 ? '✅ V2' : '❌ V1' }}
                  </p>
                </div>
                <div class="bg-gray-700 p-3 rounded-md">
                  <p class="text-sm text-gray-400">ความจุแคมป์</p>
                  <p class="font-semibold text-white">{{ campData?.capacity || 0 }} / {{ campData?.maxCapacity || 10 }}</p>
                </div>
                <div class="bg-gray-700 p-3 rounded-md">
                  <p class="text-sm text-gray-400">จำนวนชาวบ้าน</p>
                  <p class="font-semibold text-white">{{ villagerCount }}</p>
                </div>
              </div>

              <!-- Migrate Villagers Button -->
              <div v-if="campData?.villagerSchemaVersion !== 2 || hasOldVillagerIds" class="mt-4">
                <div class="bg-red-900/40 border border-red-500 rounded-lg p-3 mb-3">
                  <p class="text-red-300 text-sm">
                    <i class="fas fa-exclamation-triangle mr-1"></i>
                    ชาวบ้านยังอยู่ใน schema เก่า (V1) หรือมี villagerIds array ค้างอยู่
                  </p>
                </div>
                <button
                  @click="runCampMigrate"
                  :disabled="isUpdating"
                  class="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  <i class="fas fa-magic mr-1"></i>
                  {{ isUpdating ? 'กำลังย้าย...' : 'Migrate ชาวบ้านเป็น V2' }}
                </button>
              </div>
              <div v-else class="mt-4">
                <div class="bg-green-900/40 border border-green-500 rounded-lg p-3">
                  <p class="text-green-300 text-sm">
                    <i class="fas fa-check-circle mr-1"></i>
                    ชาวบ้านอยู่ใน schema ใหม่ (V2) แล้ว
                  </p>
                </div>
              </div>
              <p v-if="campMigrateMessage" :class="campMigrateError ? 'text-red-400' : 'text-green-400'" class="mt-3 text-sm">
                {{ campMigrateMessage }}
              </p>
            </div>

            <!-- Villager List -->
            <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <div class="flex justify-between items-center mb-4">
                <h4 class="text-sm font-semibold text-indigo-300">รายการชาวบ้าน ({{ villagerCount }} คน)</h4>
                <select v-model="villagerSortBy" class="bg-gray-700 text-indigo-200 px-2 py-1 rounded text-sm border border-gray-600">
                  <option value="level">เลเวล</option>
                  <option value="name">ชื่อ</option>
                  <option value="stamina">sta</option>
                  <option value="str">str</option>
                  <option value="agi">agi</option>
                  <option value="int">int</option>
                  <option value="luck">luck</option>
                  <option value="defense">def</option>
                </select>
              </div>

              <div v-if="sortedVillagers.length" class="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                <div
                  v-for="v in sortedVillagers"
                  :key="v.id"
                  class="bg-gray-900 bg-opacity-60 rounded-xl border border-gray-800 overflow-hidden p-3"
                >
                  <div class="flex items-center gap-3">
                    <img
                      :src="v.img || v.template?.normalFace || '/img/villagers/default.png'"
                      class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                      @error="$event.target.style.display='none'"
                    />
                    <div class="flex-1 min-w-0">
                      <p class="font-semibold text-gray-100 truncate">{{ v.name }}</p>
                      <p class="text-xs text-gray-400">Lv.{{ v.level }}</p>
                    </div>
                  </div>
                  <div class="grid grid-cols-3 gap-1 mt-2">
                    <span class="px-1 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700 text-center">
                      sta: {{ getVillagerTotalStat(v,'stamina') }}<span v-if="getVillagerUpgradeStat(v,'stamina') > 0" class="text-green-400">(+{{ getVillagerUpgradeStat(v,'stamina') }})</span>
                    </span>
                    <span class="px-1 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700 text-center">
                      str: {{ getVillagerTotalStat(v,'str') }}<span v-if="getVillagerUpgradeStat(v,'str') > 0" class="text-green-400">(+{{ getVillagerUpgradeStat(v,'str') }})</span>
                    </span>
                    <span class="px-1 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700 text-center">
                      agi: {{ getVillagerTotalStat(v,'agi') }}<span v-if="getVillagerUpgradeStat(v,'agi') > 0" class="text-green-400">(+{{ getVillagerUpgradeStat(v,'agi') }})</span>
                    </span>
                    <span class="px-1 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700 text-center">
                      int: {{ getVillagerTotalStat(v,'int') }}<span v-if="getVillagerUpgradeStat(v,'int') > 0" class="text-green-400">(+{{ getVillagerUpgradeStat(v,'int') }})</span>
                    </span>
                    <span class="px-1 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700 text-center">
                      luck: {{ getVillagerTotalStat(v,'luck') }}<span v-if="getVillagerUpgradeStat(v,'luck') > 0" class="text-green-400">(+{{ getVillagerUpgradeStat(v,'luck') }})</span>
                    </span>
                    <span class="px-1 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700 text-center">
                      def: {{ getVillagerTotalStat(v,'defense') }}<span v-if="getVillagerUpgradeStat(v,'defense') > 0" class="text-green-400">(+{{ getVillagerUpgradeStat(v,'defense') }})</span>
                    </span>
                  </div>
                </div>
              </div>
              <p v-else class="text-gray-400 text-sm">ไม่มีชาวบ้านในแคมป์</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import gameItemsData from '@/data/gameItems.json';
import gameVillagersData from '@/data/gameVillagers.json';

const setConfigs = {
  'เซ็ตทหาร': {
    items: ['หมวกทหาร', 'เสื้อกองทัพบก', 'เสื้อคลุมทหาร', 'ถุงมือทหาร', 'กางเกงทหาร', 'รองเท้าคอมแบท', 'เข็มขัดทหาร', 'แท่งเหล็กปลายมีด'],
    bonuses: { 4: { critical: 4, evasion: 4, atk: 20 }, 8: { critical: 10, evasion: 10, atk: 50 } }
  },
  'เซ็ตนักดับเพลิง': {
    items: ['หมวกนิรภัย', 'เสื้อนักดับเพลิง', 'เสื้อคลุมนักดับเพลิง', 'ถุงมือนักดับเพลิง', 'กางเกงนักดับเพลิง', 'รองเท้านักดับเพลิง', 'เข็มขัดนักดับเพลิง', 'เลื่อยไฟฟ้า'],
    bonuses: { 4: { maxHp: 1000, critical: 4, defense: 20 }, 8: { maxHp: 2500, critical: 10, defense: 50, atk: 10 } }
  },
  'เซ็ตตัวตลก': {
    items: ['ถุงมือตัวตลก', 'เสื้อตัวตลก', 'กางเกงตัวตลก', 'หมวกตัวตลก', 'รองเท้าตัวตลก'],
    bonuses: { 2: { atk: 15, critical: 10 }, 5: { atk: 40, critical: 20 } }
  },
  'เซ็ตเทควันโด': {
    items: ['เสื้อเทควันโด', 'เสื้อคลุมเทควันโด', 'กางเกงเทควันโด'],
    bonuses: { 3: { evasion: 20, atk: 30 } }
  },
  'เซ็ตนักรบไทย': {
    items: ['เกราะนักรบไทย', 'ปลอกแขนนักรบไทย', 'สนับแข้งนักรบไทย', 'หมวกนักรบไทย', 'หมวกอัศวิน', 'ดาบ'],
    bonuses: { 3: { critical: 3, evasion: 3, atk: 20 }, 4: { critical: 6, evasion: 6, atk: 30 }, 5: { critical: 10, evasion: 10, atk: 40 } }
  }
};

export default {
  name: 'AdminMigrateVerify',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const userId = ref(route.params.userId || '');

    // Tabs
    const tabs = [
      { id: 'item-migrate', name: 'ย้ายข้อมูลไอเทม', icon: 'fas fa-magic' },
      { id: 'item-verify', name: 'ตรวจสอบไอเทม', icon: 'fas fa-check-double' },
      { id: 'camp-villagers', name: 'แคมป์และชาวบ้าน', icon: 'fas fa-campground' },
    ];
    const activeTab = ref(route.query.tab || 'item-verify');

    // Loading
    const loading = ref(true);
    const error = ref(null);
    const isUpdating = ref(false);

    // Data
    const userData = ref(null);
    const characterData = ref(null);
    const characterPrivateData = ref(null);
    const campData = ref(null);

    // Inventory / Storage
    const inventoryItems = ref([]);
    const currentWeight = ref(0);
    const maxLoad = ref(100);
    const storageItems = ref([]);
    const storageWeight = ref(0);
    const storageMaxLoad = ref(100);

    // Equipped
    const equippedItems = ref([]);

    // Villagers
    const villagers = ref([]);
    const villagerSortBy = ref('level');

    // Messages
    const migrateMessage = ref('');
    const migrateError = ref(false);
    const campMigrateMessage = ref('');
    const campMigrateError = ref(false);

    // Stat keys & labels
    const statKeys = ['stamina', 'str', 'int', 'agi', 'luck', 'maxHp', 'atk', 'evasion', 'critical', 'defense'];
    const statLabels = {
      stamina: 'Stamina', str: 'Strength', int: 'Intelligence', agi: 'Agility', luck: 'Luck',
      maxHp: 'Max HP', atk: 'Attack', evasion: 'Evasion', critical: 'Critical', defense: 'Defense'
    };

    // Computed: HP & Percentages
    const playerMaxHp = computed(() => {
      if (!characterPrivateData.value) return 0;
      const stats = characterPrivateData.value;
      const stamina = (stats.itemStats?.stamina || 0) + (stats.stats?.stamina || 0) + (stats.traitStats?.stamina || 0) +
        (stats.skillStats?.stamina || 0) + (stats.titleStats?.stamina || 0) + (stats.tattooStats?.stamina || 0) +
        (stats.ghostStats?.stamina || 0) + (stats.bonusStats?.stamina || 0);
      return stamina * 20 +
        (stats.itemStats?.maxHp || 0) +
        (stats.stats?.maxHp || 0) +
        (stats.traitStats?.maxHp || 0) +
        (stats.skillStats?.maxHp || 0) +
        (stats.titleStats?.maxHp || 0) +
        (stats.tattooStats?.maxHp || 0) +
        (stats.ghostStats?.maxHp || 0) +
        (stats.bonusStats?.maxHp || 0);
    });
    const playerHp = computed(() => {
      if (!characterPrivateData.value) return 0;
      return Math.max(0, playerMaxHp.value - (characterPrivateData.value.damageTaken || 0));
    });
    const hpPercentage = computed(() => playerMaxHp.value > 0 ? (playerHp.value / playerMaxHp.value) * 100 : 0);
    const energyPercentage = computed(() => ((characterPrivateData.value?.energyLevel || 0) / 4800) * 100);
    const foodPercentage = computed(() => ((characterPrivateData.value?.foodLevel || 0) / 100) * 100);
    const happinessPercentage = computed(() => ((characterPrivateData.value?.happyLevel || 0) / 100) * 100);
    const infectionPercentage = computed(() => ((characterPrivateData.value?.infectionLevel || 0) / 100) * 100);

    // Calculated Stats
    const calculatedItemStats = computed(() => {
      const stats = { stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 };
      equippedItems.value.forEach(item => {
        const s = item.stats || {};
        const us = item.upgradeStats || {};
        const cs = item.cardStats || {};
        statKeys.forEach(k => { stats[k] += (s[k] || 0) + (us[k] || 0) + (cs[k] || 0); });
      });
      return stats;
    });
    const calculatedGhostStats = computed(() => {
      const stats = { stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 };
      // ไม่มี ghostItems loaded ในเวอร์ชันนี้ แต่จะใช้จาก characterPrivateData.ghostItems ถ้ามี
      const ghostItems = characterPrivateData.value?.ghostItems || {};
      Object.values(ghostItems).forEach(item => {
        const s = item.bonusStats || item.stats || {};
        statKeys.forEach(k => { stats[k] += s[k] || 0; });
      });
      return stats;
    });
    const calculatedBonusStats = computed(() => {
      const stats = { stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 };
      for (const [setName, config] of Object.entries(setConfigs)) {
        const equippedNames = equippedItems.value.map(item => item.name).filter(Boolean);
        const equippedInSet = equippedNames.filter(name => config.items.includes(name)).length;
        if (equippedInSet === 0) continue;
        const bonusKeys = Object.keys(config.bonuses).map(Number).sort((a, b) => b - a);
        for (const required of bonusKeys) {
          if (equippedInSet >= required) {
            const bonus = config.bonuses[required];
            for (const [stat, value] of Object.entries(bonus)) { stats[stat] = (stats[stat] || 0) + value; }
            break;
          }
        }
      }
      return stats;
    });
    const calculatedBagWeight = computed(() => inventoryItems.value.reduce((sum, item) => sum + (item.weight || 0) * (item.quantity || 1), 0));
    const calculatedStorageWeight = computed(() => storageItems.value.reduce((sum, item) => sum + (item.weight || 0) * (item.quantity || 1), 0));

    // Matching checks
    const isItemStatsMatching = computed(() => statKeys.every(k => (characterPrivateData.value?.itemStats?.[k] || 0) === (calculatedItemStats.value[k] || 0)));
    const isGhostStatsMatching = computed(() => statKeys.every(k => (characterPrivateData.value?.ghostStats?.[k] || 0) === (calculatedGhostStats.value[k] || 0)));
    const isBonusStatsMatching = computed(() => statKeys.every(k => (characterPrivateData.value?.bonusStats?.[k] || 0) === (calculatedBonusStats.value[k] || 0)));
    const isBagWeightMatching = computed(() => currentWeight.value === calculatedBagWeight.value);
    const isStorageWeightMatching = computed(() => storageWeight.value === calculatedStorageWeight.value);

    // Set Progress
    const setProgress = computed(() => {
      const progress = [];
      const equippedNames = equippedItems.value.map(item => item.name).filter(Boolean);
      for (const [setName, config] of Object.entries(setConfigs)) {
        const equippedInSet = config.items.filter(name => equippedNames.includes(name)).length;
        if (equippedInSet === 0) continue;
        const bonusKeys = Object.keys(config.bonuses).map(Number).sort((a, b) => b - a);
        let currentBonus = {};
        for (const required of bonusKeys) {
          if (equippedInSet >= required) { currentBonus = config.bonuses[required]; break; }
        }
        progress.push({ name: setName, equippedCount: equippedInSet, totalItems: config.items.length, currentBonus });
      }
      return progress;
    });

    // Villagers
    const villagerCount = computed(() => villagers.value.length);
    const hasOldVillagerIds = computed(() => Array.isArray(campData.value?.villagerIds) && campData.value.villagerIds.length > 0);
    const sortedVillagers = computed(() => {
      const list = [...villagers.value];
      list.sort((a, b) => {
        if (villagerSortBy.value === 'name') return (a.name || '').localeCompare(b.name || '');
        if (villagerSortBy.value === 'level') return (b.level || 0) - (a.level || 0);
        return getVillagerTotalStat(b, villagerSortBy.value) - getVillagerTotalStat(a, villagerSortBy.value);
      });
      return list;
    });

    // Old schema detection
    const hasOldItemSchema = computed(() => {
      if (!characterPrivateData.value) return false;
      const cp = characterPrivateData.value;
      return Array.isArray(cp.itemReferences) || Array.isArray(cp.mechanicalItemIds) || Array.isArray(cp.ghostItemIds);
    });

    // Helpers
    const mapItemsObject = (itemsObj) => {
      return Object.entries(itemsObj || {}).map(([instanceId, itemData]) => {
        const tpl = gameItemsData[itemData.templateId] || {};
        return { id: instanceId, instanceId, name: tpl.name || itemData.templateId, ...tpl, ...itemData.states, quantity: 1 };
      });
    };
    const formatBonusObject = (obj) => Object.entries(obj || {}).map(([k, v]) => `${statLabels[k] || k}: ${v}`).join(', ') || 'ไม่มี';
    const getVillagerTotalStat = (v, stat) => {
      const base = v.template?.[stat] || v[stat] || 0;
      const upgrade = v.states?.upgradeStats?.[stat] || 0;
      return base + upgrade;
    };
    const getVillagerUpgradeStat = (v, stat) => v.states?.upgradeStats?.[stat] || 0;

    // Fetch data
    const fetchData = async () => {
      loading.value = true;
      error.value = null;
      try {
        // User data
        const userDoc = await projectFirestore.collection('users').doc(userId.value).get();
        if (userDoc.exists) userData.value = userDoc.data();

        // Character data
        const charDoc = await projectFirestore.collection('characters').doc(userId.value).get();
        if (charDoc.exists) characterData.value = charDoc.data();

        // Character private
        const charPrivateDoc = await projectFirestore.collection('characterPrivate').doc(userId.value).get();
        if (charPrivateDoc.exists) {
          const cpd = charPrivateDoc.data();
          characterPrivateData.value = {
            ...cpd,
            itemStats: cpd.itemStats || {}, bonusStats: cpd.bonusStats || {}, ghostStats: cpd.ghostStats || {},
            stats: cpd.stats || {}, traitStats: cpd.traitStats || {}, skillStats: cpd.skillStats || {},
            titleStats: cpd.titleStats || {}, tattooStats: cpd.tattooStats || {}
          };
          // Equipped items
          const equippedItemsData = cpd.equippedItems || {};
          equippedItems.value = Object.entries(equippedItemsData).map(([instanceId, item]) => {
            const tpl = gameItemsData[item.templateId] || {};
            return { id: instanceId, instanceId, name: tpl.name || item.templateId, ...tpl, ...item.states };
          });
        }

        // Bags
        const bagDoc = await projectFirestore.collection('bags').doc(userId.value).get();
        if (bagDoc.exists) {
          const bd = bagDoc.data();
          inventoryItems.value = mapItemsObject(bd.items);
          currentWeight.value = bd.currentWeight || 0;
          maxLoad.value = bd.maxLoad || 100;
        }

        // Storages
        const storageDoc = await projectFirestore.collection('storages').doc(userId.value).get();
        if (storageDoc.exists) {
          const sd = storageDoc.data();
          storageItems.value = mapItemsObject(sd.items);
          storageWeight.value = sd.currentWeight || 0;
          storageMaxLoad.value = sd.maxLoad || 100;
        }

        // Camp data
        const campDoc = await projectFirestore.collection('camps').doc(userId.value).get();
        if (campDoc.exists) {
          campData.value = campDoc.data();
          const campVillagers = campDoc.data().villagers || {};
          villagers.value = Object.entries(campVillagers).map(([id, v]) => {
            const tpl = gameVillagersData[v.templateId] || {};
            return { id, ...tpl, ...v, template: tpl, states: v.states || {} };
          });
        }
      } catch (err) {
        console.error(err);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
      } finally {
        loading.value = false;
      }
    };

    // Update methods
    const updateItemStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          itemStats: calculatedItemStats.value
        });
      } catch (err) { error.value = err.message; }
      finally { isUpdating.value = false; }
    };
    const updateGhostStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          ghostStats: calculatedGhostStats.value
        });
      } catch (err) { error.value = err.message; }
      finally { isUpdating.value = false; }
    };
    const updateBonusStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          bonusStats: calculatedBonusStats.value
        });
      } catch (err) { error.value = err.message; }
      finally { isUpdating.value = false; }
    };

    // Migrate actions
    const runItemMigrate = async () => {
      isUpdating.value = true;
      migrateMessage.value = '';
      migrateError.value = false;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'adminMigratePlayer', targetUid: userId.value });
        migrateMessage.value = result.data?.message || 'ย้ายข้อมูลสำเร็จ';
        await fetchData();
      } catch (err) {
        migrateError.value = true;
        migrateMessage.value = err.message || 'เกิดข้อผิดพลาด';
      } finally {
        isUpdating.value = false;
      }
    };
    const resetItemMigrate = async () => {
      isUpdating.value = true;
      migrateMessage.value = '';
      migrateError.value = false;
      try {
        await projectFirestore.collection('users').doc(userId.value).update({ migratedToItemV2: false });
        migrateMessage.value = 'รีเซ็ตสถานะสำเร็จ ผู้เล่นจะเห็นหน้าย้ายข้อมูลอัตโนมัติ';
        await fetchData();
      } catch (err) {
        migrateError.value = true;
        migrateMessage.value = err.message || 'เกิดข้อผิดพลาด';
      } finally {
        isUpdating.value = false;
      }
    };
    const runCampMigrate = async () => {
      isUpdating.value = true;
      campMigrateMessage.value = '';
      campMigrateError.value = false;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'outpostActions', subAction: 'adminMigrateCampVillagers', targetUid: userId.value });
        campMigrateMessage.value = result.data?.message || 'ย้ายข้อมูลชาวบ้านสำเร็จ';
        await fetchData();
      } catch (err) {
        campMigrateError.value = true;
        campMigrateMessage.value = err.message || 'เกิดข้อผิดพลาด';
      } finally {
        isUpdating.value = false;
      }
    };

    onMounted(fetchData);
    watch(() => route.params.userId, (newId) => { if (newId) { userId.value = newId; fetchData(); } });
    watch(() => route.query.tab, (newTab) => { if (newTab) activeTab.value = newTab; });

    return {
      userId, tabs, activeTab,
      loading, error, isUpdating,
      userData, characterData, characterPrivateData, campData,
      inventoryItems, currentWeight, maxLoad, storageItems, storageWeight, storageMaxLoad,
      equippedItems, villagers, villagerSortBy,
      migrateMessage, migrateError, campMigrateMessage, campMigrateError,
      statKeys, statLabels,
      playerHp, playerMaxHp, hpPercentage, energyPercentage, foodPercentage, happinessPercentage, infectionPercentage,
      calculatedItemStats, calculatedGhostStats, calculatedBonusStats, calculatedBagWeight, calculatedStorageWeight,
      isItemStatsMatching, isGhostStatsMatching, isBonusStatsMatching, isBagWeightMatching, isStorageWeightMatching,
      setProgress, villagerCount, hasOldVillagerIds, sortedVillagers, hasOldItemSchema,
      formatBonusObject, getVillagerTotalStat, getVillagerUpgradeStat,
      updateItemStats, updateGhostStats, updateBonusStats,
      runItemMigrate, resetItemMigrate, runCampMigrate,
      fetchData
    };
  }
};
</script>

<style scoped>
.text-xxs { font-size: 0.65rem; line-height: 0.9rem; }
</style>
